import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Typography,
  Paper,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SelectChangeEvent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { eventRegisterInitialize, eventRegister } from '../../api/api';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { OutletContextType } from '../root';

/**
 * 体育館情報
 */
export type ArenaInfo = {
  id: string,
  name: string
};

/**
 * イベント情報
 */
export type TKGEvent = {
  user_id: string | null,
  event_name: string | null,
  event_date: string | null,
  b_start_time: string | null,
  b_end_time: string | null,
  d_start_time: string | null,
  d_end_time: string | null,
  b_entry_fee: string | null,
  d_entry_fee: string | null,
  arena_id: string | null,
  restaurant: string | null,
  restaurant_url: string | null,
  question_content: string | null,
  event_content: string | null,
  belongings: string | null,
  notes: string | null,
  meeting_time: string | null,
  capacity_num: string | null,
  man_capacity_num: string | null,
  woman_capacity_num: string | null,
  memo: string | null,
  public_flag: boolean | null,
  is_broadcast: boolean | null
};

/**
 * 06:00 〜 23:45 の間で 30 分刻みの時間リストを作成
 * @returns 時間リスト
 */
const generateTimeOptions = () => {
  const times = [ '' ];
  for (let hour = 6; hour < 24; hour++) {
    for (let minute of [0, 30]) {
      times.push(
        `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
      );
    }
  }
  return times;
};

/**
 * 【TKG_RESERVE_0240】
 * @returns イベント新規登録画面
 */
export const EventRegister = () => {
  const navigate = useNavigate();
  const [reloadTime, setReloadTime] = useState<Date>();
  const [errorMessage, setErrorMessage] = useState(''); // 画面上部のバリデーションエラーメッセージを格納する変数
  const [processResultMessage, setProcessResultMessage] = useState<string>('');
  const [openResultDialog, setOpenResultDialog] = useState<boolean>(false);
  const [arenaOptions, setArenaOptions] = useState<ArenaInfo[]>([]);
  const { userId } = useOutletContext<OutletContextType>();
  const [formData, setFormData] = useState({
    user_id: userId || null,
    event_name: null, 
    event_date: null,
    b_start_time: null,
    b_end_time: null,
    d_start_time: null,
    d_end_time: null,
    b_entry_fee: '1000',
    d_entry_fee: '2500',
    arena_id: '',
    restaurant: null,
    restaurant_url: null,
    question_content: '',
    event_content: '',
    belongings: '・バスケットシューズ(体育館履き)必須\n・着替え\n・タオル\n・飲み物\n・ボール（持っている人）\n・TKG公式リバーシブル（持っている人）',
    notes: '・集合時間以降にお越しください\n・施設入口の受付は不要です\n・遅刻の場合は公式LINEに【到着時刻】を送ってください\n・体育館に入ったらチーム分け表を確認してください\n・初参加の方は体育館に入ったら「代表者いますか？」と話しかけてください',
    meeting_time: null,
    capacity_num: '30',
    man_capacity_num: '30',
    woman_capacity_num: '30',
    memo: '',
    public_flag: false,
    is_broadcast: false
  });

  useEffect(() => {
    // 初期表示処理を行いサーバーサイドから体育館マスタを取得する
    eventRegisterInitialize(userId).then(response => {
      const newArenaInfo = response.map((arenaEntity: any) => ({
        id: arenaEntity.arenaId,
        name: arenaEntity.arenaName,
      }));
      setArenaOptions(newArenaInfo);
    });
  }, [userId, reloadTime]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = () => {
    // 必須項目の検証
    const isError = (
        !formData.event_name
        || !formData.event_date
        || !formData.meeting_time
        || !formData.capacity_num
        || !formData.man_capacity_num
        || !formData.woman_capacity_num
        || (!formData.b_start_time && !formData.d_start_time)
    )

    if (isError) {
      setErrorMessage('必須項目をすべて入力してください。');
      return;
    } else {
      setErrorMessage('');
    }

    // 日付と時刻を結合して新しいフォーマットに変換
    const formatDateTime = (date: string | null, time: string | null) => {
      if (!date || !time) {
        return `0000/00/00 00:00:00`;
      } else {
        const [year, month, day] = date?.split('-');
        const [hour, minute] = time?.split(':');
        return `${year}/${month}/${day} ${hour}:${minute}:00`;
      }
    };

    const formattedData = {
      ...formData,
      question_content: formData.question_content?.replace(/\r\n|\r/g, "\n"),
      event_content: formData.event_content?.replace(/\r\n|\r/g, "\n"),
      belongings: formData.belongings?.replace(/\r\n|\r/g, "\n"),
      notes: formData.notes?.replace(/\r\n|\r/g, "\n"),
      memo: formData.memo?.replace(/\r\n|\r/g, "\n"),
      b_start_time: formData.b_start_time ? formatDateTime(formData.event_date, formData.b_start_time) : null,
      b_end_time: formData.b_end_time ? formatDateTime(formData.event_date, formData.b_end_time) : null,
      d_start_time: formData.d_start_time ? formatDateTime(formData.event_date, formData.d_start_time) : null,
      d_end_time: formData.d_end_time ? formatDateTime(formData.event_date, formData.d_end_time) : null,
      meeting_time: formData.meeting_time ? formatDateTime(formData.event_date, formData.meeting_time) : null,
    };

    // イベント登録処理を実施する
    eventRegister(formattedData).then(response => {
      if (response.status !== 200) {
        setProcessResultMessage(response.data.message);
        setOpenResultDialog(true);
      } else {
        setProcessResultMessage('イベントの登録に成功しました！');
        setOpenResultDialog(true);
      }
    });
  }

  return (
    <>
    <Box sx={{ paddingTop: '10vh' }}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon sx={{transition: 'transform 0.3s ease'}} />}
        onClick={() => navigate("/mng/portal")}
        sx={{
            ':hover': {
                backgroundColor: '#1565c0',
                color: 'white'
            }
        }}
      >
        ポータルに戻る
      </Button>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'center', paddingBottom: '5vh' }}>
      <Paper sx={{ padding: 3, width: '100%', maxWidth: '600px', borderRadius: '16px', backgroundColor: '#f3f4f6' }}>
        <Typography variant="h4" sx={{ textAlign: 'center', color: '#ffb74d', marginBottom: '1rem', fontWeight: 'bold' }}>
          イベント新規登録
        </Typography>

        {/* エラーメッセージ */}
        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: '1rem' }}>
            {errorMessage}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Event Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="イベント名"
              variant="outlined"
              name="event_name"
              value={formData.event_name}
              onChange={handleChange}
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Event Date */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="イベント開催日"
              variant="outlined"
              name="event_date"
              type="date"
              value={formData.event_date}
              onChange={handleChange}
              required
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Basketball Start Time */}
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="バスケ開始時刻"
              variant="outlined"
              name="b_start_time"
              value={formData.b_start_time}
              onChange={handleChange}
              sx={{ backgroundColor: "#fff" }}
              InputLabelProps={{ shrink: true }}
            >
              {generateTimeOptions().map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Basketball End Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="バスケ終了時刻"
              variant="outlined"
              name="b_end_time"
              select
              value={formData.b_end_time}
              onChange={handleChange}
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            >
              {generateTimeOptions().map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Drinking Start Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="飲み会開始時刻"
              variant="outlined"
              name="d_start_time"
              select
              value={formData.d_start_time}
              onChange={handleChange}
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            >
              {generateTimeOptions().map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Drinking End Time */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="飲み会終了時刻"
              variant="outlined"
              name="d_end_time"
              select
              value={formData.d_end_time}
              onChange={handleChange}
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            >
              {generateTimeOptions().map((time) => (
                <MenuItem key={time} value={time}>
                  {time}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Meeting Time */}
          <Grid item xs={6}>
            <TextField
              label="集合時間"
              variant="outlined"
              name="meeting_time"
              type="time"
              value={formData.meeting_time}
              onChange={handleChange}
              required
              fullWidth
              sx={{ backgroundColor: '#fff' }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Capacity */}
          <Grid item xs={6}>
            <TextField
              label="キャパシティ"
              variant="outlined"
              name="capacity_num"
              value={formData.capacity_num}
              onChange={handleChange}
              fullWidth
              type="number"
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* man_capacity_num */}
          <Grid item xs={6}>
            <TextField
              label="男性キャパシティ"
              variant="outlined"
              name="man_capacity_num"
              value={formData.man_capacity_num}
              onChange={handleChange}
              fullWidth
              type="number"
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* woman_capacity_num */}
          <Grid item xs={6}>
            <TextField
              label="女性キャパシティ"
              variant="outlined"
              name="woman_capacity_num"
              value={formData.woman_capacity_num}
              onChange={handleChange}
              fullWidth
              type="number"
              required
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Basketball Entry Fee */}
          <Grid item xs={6}>
            <TextField
              label="バスケ参加費"
              variant="outlined"
              name="b_entry_fee"
              value={formData.b_entry_fee}
              onChange={handleChange}
              fullWidth
              type="number"
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Drinking Entry Fee */}
          <Grid item xs={6}>
            <TextField
              label="飲み会参加費"
              variant="outlined"
              name="d_entry_fee"
              value={formData.d_entry_fee}
              onChange={handleChange}
              fullWidth
              type="number"
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Arena */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>体育館</InputLabel>
              <Select
                name="arena_id"
                value={formData.arena_id}
                onChange={handleSelectChange}
                sx={{ backgroundColor: '#fff' }}
              >
                <MenuItem key='' value='' />
                {arenaOptions && arenaOptions.length > 0 ? arenaOptions.map((arena) => (
                  <MenuItem key={arena.id} value={arena.id}>
                    {arena.name}
                  </MenuItem>
                )) : <></>}
              </Select>
            </FormControl>
          </Grid>

          {/* Restaurant */}
          <Grid item xs={12}>
            <TextField
              label="飲み会場所"
              variant="outlined"
              name="restaurant"
              value={formData.restaurant}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Restaurant URL */}
          <Grid item xs={12}>
            <TextField
              label="飲み会場所 URL"
              variant="outlined"
              name="restaurant_url"
              value={formData.restaurant_url}
              onChange={handleChange}
              fullWidth
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* belongings */}
          <Grid item xs={12}>
            <TextField
              label="持ち物"
              variant="outlined"
              name="belongings"
              value={formData.belongings}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* notes */}
          <Grid item xs={12}>
            <TextField
              label="注意事項"
              variant="outlined"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Event Content */}
          <Grid item xs={12}>
            <TextField
              label="イベント内容"
              variant="outlined"
              name="event_content"
              value={formData.event_content}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Question Content */}
          <Grid item xs={12}>
            <TextField
              label="質問内容"
              variant="outlined"
              name="question_content"
              value={formData.question_content}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* Memo */}
          <Grid item xs={12}>
            <TextField
              label="管理者用メモ"
              variant="outlined"
              name="memo"
              value={formData.memo}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ backgroundColor: '#fff' }}
            />
          </Grid>

          {/* public flag */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.public_flag}
                  onChange={handleChange}
                  name="public_flag"
                />
              }
              label="公開する"
            />
          </Grid>

          {/* Broadcast */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_broadcast}
                  onChange={handleChange}
                  name="is_broadcast"
                />
              }
              label="配信する"
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={handleSubmit}
              sx={{
                backgroundColor: '#FF9800',
                ':hover': {
                  backgroundColor: '#FFB74D',
                },
              }}
            >
              新規登録
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>

    {/* 処理結果通知ダイアログ */}
    <Dialog open={openResultDialog} onClose={() => setOpenResultDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
            }}
          >
            <InfoIcon fontSize="large" />
            <Typography variant="h6">処理結果</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <pre>{processResultMessage}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setOpenResultDialog(false);setReloadTime(new Date());navigate("/mng/portal", { state: { user_id: userId } })}} variant="contained" color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
